import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Tabs,
  Tab,
  Card,
  CardContent,
  GridLegacy as Grid,
  Avatar,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Person as PersonIcon,
  EmojiEvents as TrophyIcon,
  ShoppingBag as ShopIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Redeem as PrizesIcon,
  Assignment as ChallengesIcon,
  Groups as TeamsIcon,
  Flag as GoalsIcon,
  Star as PointsIcon,
  Share as ShareIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Help as HelpIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { teamService } from '../../services/teamService';
import { avatarService } from '../../services/avatarService';
import { badgeService } from '../../services/badgeService';
import { UserAvatar, AvatarItem, Badge, UserBadge, PersonalRecord, Team, UserInventory } from '../../types';
import { Timestamp } from 'firebase/firestore';
import ReadyPlayerMeAvatar from './ReadyPlayerMeAvatar';
import AvatarCreatorModal from './AvatarCreatorModal';
import { AvatarCallbackService } from '../../services/avatarCallbackService';
import { ReadyPlayerMeService } from '../../services/readyPlayerMeService';
import ReadyPlayerMeIframe from './ReadyPlayerMeIframe';
import PrizesPage from '../prizes/PrizesPage';
import ChallengesMissionsPage from '../challenges/ChallengesMissionsPage';
import TeamsPage from '../teams/TeamsPage';
import GoalsPage from '../goals/GoalsPage';
import PointsPage from '../points/PointsPage';
import ShareProfileDialog from './ShareProfileDialog';
import ProfilePrivacyDialog from './ProfilePrivacyDialog';
import AddEditPRDialog from './AddEditPRDialog';
import NotificationSettings from './NotificationSettings';
import WorkflowSettings from './WorkflowSettings';
import DeleteAccountDialog from './DeleteAccountDialog';
import SeasonHistorySection from './SeasonHistorySection';
import EditDisplayNameDialog from '../common/EditDisplayNameDialog';
import { userService } from '../../services/userService';
import { formatPRTimeDisplay } from '../../utils/prTimeFormat';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ProfilePage: React.FC = () => {
  const { user, refreshCurrentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [userAvatar, setUserAvatar] = useState<UserAvatar | null>(null);
  const [userBadges, setUserBadges] = useState<UserBadge[]>([]);
  const [userPRs, setUserPRs] = useState<PersonalRecord[]>([]);
  const [availableItems, setAvailableItems] = useState<AvatarItem[]>([]);
  const [availableBadges, setAvailableBadges] = useState<Badge[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [editAvatarOpen, setEditAvatarOpen] = useState(false);
  const [equipItemsOpen, setEquipItemsOpen] = useState(false);
  const [userInventory, setUserInventory] = useState<UserInventory | null>(null);
  const [avatarCreatorOpen, setAvatarCreatorOpen] = useState(false);
  const [showIframeModal, setShowIframeModal] = useState(false);
  const [lockedAssets, setLockedAssets] = useState<string[]>([]);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);
  const [prDialogOpen, setPrDialogOpen] = useState(false);
  const [editingPR, setEditingPR] = useState<PersonalRecord | null>(null);
  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = useState(false);
  const [editDisplayNameDialogOpen, setEditDisplayNameDialogOpen] = useState(false);
  // Hook to track screen size
  const [screenWidth, setScreenWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  
  React.useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update avatar position when screen size changes
  React.useEffect(() => {
    const savedPosition = localStorage.getItem('avatarPosition');
    
    const getResponsiveAvatarPosition = () => {
      if (screenWidth < 600) { // Mobile
        return { x: 100, y: 120 };
      } else if (screenWidth < 900) { // Tablet
        return { x: 150, y: 160 };
      } else { // Desktop
        return { x: 200, y: 200 };
      }
    };
    
    if (!savedPosition) {
      // No saved position - use responsive default
      const newPosition = getResponsiveAvatarPosition();
      setAvatarPosition(newPosition);
    } else {
      // Scale saved position based on screen size
      const saved = JSON.parse(savedPosition);
      const basePosition = { x: 200, y: 200 }; // Base desktop position
      
      let scaleX = 1;
      let scaleY = 1;
      
      if (screenWidth < 600) { // Mobile
        scaleX = 0.5;
        scaleY = 0.6;
      } else if (screenWidth < 900) { // Tablet
        scaleX = 0.75;
        scaleY = 0.8;
      }
      
      // Calculate relative position from base
      const relativeX = saved.x - basePosition.x;
      const relativeY = saved.y - basePosition.y;
      
      // Apply scaling and add to new base position
      const newBasePosition = getResponsiveAvatarPosition();
      const newPosition = {
        x: newBasePosition.x + (relativeX * scaleX),
        y: newBasePosition.y + (relativeY * scaleY)
      };
      
      setAvatarPosition(newPosition);
      localStorage.setItem('avatarPosition', JSON.stringify(newPosition));
    }
  }, [screenWidth]);

  const [avatarPosition, setAvatarPosition] = useState(() => {
    // Load saved avatar position from localStorage
    const saved = localStorage.getItem('avatarPosition');
    if (saved) {
      return JSON.parse(saved);
    }
    
    // Default responsive positioning
    const defaultPos = { x: 200, y: 200 }; // Base desktop position
    if (typeof window !== 'undefined') {
      const width = window.innerWidth;
      if (width < 600) { // Mobile
        return { x: 100, y: 120 };
      } else if (width < 900) { // Tablet
        return { x: 150, y: 160 };
      }
    }
    return defaultPos;
  });
  const [isDraggingAvatar, setIsDraggingAvatar] = useState(false);
  const [isAvatarRotating, setIsAvatarRotating] = useState(false);
  const [dragStart, setDragStart] = useState<{ x: number; y: number; position: { x: number; y: number } } | null>(null);
  const [decorationPositions, setDecorationPositions] = useState<Record<string, { x: number; y: number }>>(() => {
    // Load saved decoration positions from localStorage
    const saved = localStorage.getItem('decorationPositions');
    return saved ? JSON.parse(saved) : {};
  });

  // Update decoration positions when screen size changes
  React.useEffect(() => {
    if (!userAvatar || availableItems.length === 0) return;
    
    const savedPositions = localStorage.getItem('decorationPositions');
    const newPositions: Record<string, { x: number; y: number }> = {};
    
    // Get current equipped decorations
    const backgroundItems = availableItems.filter(item => 
      item.category === 'background' && userAvatar[item.category] === item.id
    );
    const accessoryItems = availableItems.filter(item => 
      item.category === 'accessory' && userAvatar[item.category] === item.id
    );
    
    // Default base positions for desktop
    const backgroundBasePositions = [
      { x: 20, y: 20 },    // Top-left
      { x: 380, y: 20 },   // Top-right
      { x: 20, y: 380 },   // Bottom-left
      { x: 380, y: 380 }   // Bottom-right
    ];
    
    const accessoryBasePositions = [
      { x: 50, y: 100 },   // Top-left
      { x: 470, y: 100 },  // Top-right
      { x: 50, y: 420 },   // Bottom-left
      { x: 470, y: 420 }   // Bottom-right
    ];
    
    // Get scaling factors
    let scaleX = 1;
    let scaleY = 1;
    
    if (screenWidth < 600) { // Mobile
      scaleX = 0.5;
      scaleY = 0.6;
    } else if (screenWidth < 900) { // Tablet
      scaleX = 0.75;
      scaleY = 0.8;
    }
    
    // Update background decorations
    backgroundItems.forEach((item, index) => {
      const savedPos = savedPositions ? JSON.parse(savedPositions)[item.id] : null;
      const basePos = backgroundBasePositions[index % backgroundBasePositions.length];
      
      if (!savedPos) {
        // No saved position - use scaled default
        newPositions[item.id] = {
          x: basePos.x * scaleX,
          y: basePos.y * scaleY
        };
      } else {
        // Scale saved position relative to base
        const relativeX = savedPos.x - basePos.x;
        const relativeY = savedPos.y - basePos.y;
        
        newPositions[item.id] = {
          x: (basePos.x * scaleX) + (relativeX * scaleX),
          y: (basePos.y * scaleY) + (relativeY * scaleY)
        };
      }
    });
    
    // Update accessory decorations
    accessoryItems.forEach((item, index) => {
      const savedPos = savedPositions ? JSON.parse(savedPositions)[item.id] : null;
      const basePos = accessoryBasePositions[index % accessoryBasePositions.length];
      
      if (!savedPos) {
        // No saved position - use scaled default
        newPositions[item.id] = {
          x: basePos.x * scaleX,
          y: basePos.y * scaleY
        };
      } else {
        // Scale saved position relative to base
        const relativeX = savedPos.x - basePos.x;
        const relativeY = savedPos.y - basePos.y;
        
        newPositions[item.id] = {
          x: (basePos.x * scaleX) + (relativeX * scaleX),
          y: (basePos.y * scaleY) + (relativeY * scaleY)
        };
      }
    });
    
    if (Object.keys(newPositions).length > 0) {
      setDecorationPositions(newPositions);
      localStorage.setItem('decorationPositions', JSON.stringify(newPositions));
    }
  }, [screenWidth, userAvatar, availableItems]);
  const [decorationSizes, setDecorationSizes] = useState<Record<string, { width: number; height: number }>>(() => {
    // Load saved decoration sizes from localStorage
    const saved = localStorage.getItem('decorationSizes');
    return saved ? JSON.parse(saved) : {};
  });
  const [draggedDecoration, setDraggedDecoration] = useState<string | null>(null);
  const [decorationDragStart, setDecorationDragStart] = useState<{ x: number; y: number; position: { x: number; y: number } } | null>(null);
  const [resizedDecoration, setResizedDecoration] = useState<string | null>(null);
  const [decorationResizeStart, setDecorationResizeStart] = useState<{ x: number; y: number; size: { width: number; height: number } } | null>(null);
  const [avatarForm, setAvatarForm] = useState({
    skinTone: '#FDBCB4',
    hairColor: '#8B4513',
    eyeColor: '#4A90E2',
    hat: '',
    shirt: '',
    pants: '',
    shoes: '',
    accessory: '',
    background: ''
  });

  // Depend on user?.id only so we don't re-run when user object reference changes (e.g. token refresh), which was causing reload loops
  useEffect(() => {
    if (user?.id) {
      loadProfileData().catch((err) => {
        console.warn('Profile data loading failed, showing empty state:', err);
        setLoading(false);
      });
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle Ready Player Me callback when component mounts or URL changes. Depend on user?.id and teams.length to avoid re-running when user object reference changes.
  useEffect(() => {
    const handleAvatarCallback = async () => {
      if (!user?.id || teams.length === 0) return;

      console.log('Checking for ReadyPlayerMe callback...');
      console.log('Current URL:', window.location.href);
      console.log('URL params:', new URLSearchParams(window.location.search));

      // Check for stored avatar data from ReadyPlayerMe events
      const storedAvatarData = sessionStorage.getItem('readyPlayerMeAvatarData');
      if (storedAvatarData) {
        console.log('Found stored avatar data from ReadyPlayerMe events');
        try {
          const avatarData = JSON.parse(storedAvatarData);
          console.log('Stored avatar data:', avatarData);
          
          if (avatarData.avatarUrl && avatarData.avatarId) {
            console.log('Processing stored avatar data');
            
            setLoading(true);
            
            // Add cache-busting parameter to force refresh
            const cacheBustedUrl = `${avatarData.avatarUrl}?t=${Date.now()}`;
            
            // Update the user's avatar data
            const updatedAvatar: UserAvatar = {
              ...userAvatar!,
              readyPlayerMeAvatarId: avatarData.avatarId,
              readyPlayerMeUrl: cacheBustedUrl,
              avatarUrl: cacheBustedUrl,
              updatedAt: Timestamp.now()
            };
            
            console.log('Updated avatar data:', updatedAvatar);
            
            // Save to database
            await avatarService.saveUserAvatar(user.id, teams[0].id, updatedAvatar);
            
            // Update local state
            setUserAvatar(updatedAvatar);
            
            console.log('Avatar saved successfully from stored data');
            
            // Clear stored data
            sessionStorage.removeItem('readyPlayerMeAvatarData');
            sessionStorage.removeItem('readyPlayerMeSession');
            
            // Show success message
            setSuccess('Avatar updated successfully!');
            setError('');
            
            // Force a small delay to ensure the avatar component re-renders
            setTimeout(() => {
              console.log('Forcing avatar refresh from stored data...');
              setUserAvatar(prev => prev ? { ...prev, updatedAt: Timestamp.now() } : prev);
            }, 100);
            
            setLoading(false);
            return;
          }
        } catch (error) {
          console.error('Failed to parse stored avatar data:', error);
          sessionStorage.removeItem('readyPlayerMeAvatarData');
        }
      }

      // Check if we have callback parameters
      if (AvatarCallbackService.hasAvatarCallbackParams()) {
        const urlParams = AvatarCallbackService.getAvatarCallbackParams();
        console.log('ReadyPlayerMe callback parameters found:', urlParams);
        
        // Check if this is a Ready Player Me callback
        if (urlParams.get('callback') === 'readyplayer') {
          const teamId = teams[0].id;
          console.log('Processing ReadyPlayerMe callback for team:', teamId);
          
          try {
            setLoading(true);
            const result = await AvatarCallbackService.handleAvatarCallback(
              urlParams,
              user.id,
              teamId
            );

            console.log('Avatar callback result:', result);

            if (result.success && result.avatar) {
              // Update the avatar state
              setUserAvatar(result.avatar);
              // Clear the URL parameters
              AvatarCallbackService.clearAvatarCallbackParams();
              // Show success message
              setSuccess('Avatar updated successfully!');
              setError(''); // Clear any existing errors
            } else {
              setError(result.error || 'Failed to save avatar from Ready Player Me');
            }
          } catch (err) {
            console.error('Avatar callback error:', err);
            setError('Failed to process avatar callback');
          } finally {
            setLoading(false);
          }
        }
      } else {
        console.log('No ReadyPlayerMe callback parameters found');
        
        // Check if this is a page reload after ReadyPlayerMe window was closed
        const readyPlayerMeSession = sessionStorage.getItem('readyPlayerMeSession');
        if (readyPlayerMeSession === 'active') {
          console.log('Page reloaded after ReadyPlayerMe session, showing success message');
          setSuccess('Avatar editor session completed! Your avatar should update automatically.');
          sessionStorage.removeItem('readyPlayerMeSession');
        }
      }
    };

    handleAvatarCallback();
  }, [user?.id, teams.length]); // eslint-disable-line react-hooks/exhaustive-deps -- avoid [user] to prevent loop when auth context updates user reference

  const loadProfileData = async () => {
    if (!user) return;

    setLoading(true);
    setError('');

    try {
      // Get user's team
      const userTeams = await teamService.getTeamsByAthlete(user.id);
      if (userTeams.length === 0) {
        setError('You need to be part of a team to view your profile.');
        setLoading(false);
        return;
      }

      setTeams(userTeams);
      const teamId = userTeams[0].id;

      // Load data with individual error handling for each service
      const results = await Promise.allSettled([
        avatarService.getUserAvatar(user.id, teamId),
        badgeService.getUserBadges(user.id, teamId),
        avatarService.getUserPRs(user.id, teamId),
        avatarService.getAvailableItems(teamId),
        badgeService.getAvailableBadges(teamId),
        avatarService.getUserInventory(user.id, teamId),
      ]);

      // Extract results and handle individual failures
      const [avatarResult, badgesResult, prsResult, itemsResult, badgeTemplatesResult, inventoryResult] = results;
      
      setUserAvatar(avatarResult.status === 'fulfilled' ? avatarResult.value : null);
      setUserBadges(badgesResult.status === 'fulfilled' ? badgesResult.value : []);
      setUserPRs(prsResult.status === 'fulfilled' ? prsResult.value : []);
      setAvailableItems(itemsResult.status === 'fulfilled' ? itemsResult.value : []);
      setAvailableBadges(badgeTemplatesResult.status === 'fulfilled' ? badgeTemplatesResult.value : []);
      setUserInventory(inventoryResult.status === 'fulfilled' ? inventoryResult.value : null);

      // Log any individual failures for debugging
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const serviceNames = ['getUserAvatar', 'getUserBadges', 'getUserPRs', 'getAvailableItems', 'getAvailableBadges', 'getUserInventory'];
          console.warn(`${serviceNames[index]} failed:`, result.reason);
        }
      });
    } catch (err: any) {
      console.error('Error loading profile data:', err);
      setError(err.message || 'Failed to load profile data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEditAvatarOpen = () => {
    // Initialize form with current avatar data or defaults
    if (userAvatar) {
      setAvatarForm({
        skinTone: userAvatar.skinTone || '#FDBCB4',
        hairColor: userAvatar.hairColor || '#8B4513',
        eyeColor: userAvatar.eyeColor || '#4A90E2',
        hat: userAvatar.hat || '',
        shirt: userAvatar.shirt || '',
        pants: userAvatar.pants || '',
        shoes: userAvatar.shoes || '',
        accessory: userAvatar.accessory || '',
        background: userAvatar.background || ''
      });
    } else {
      // Reset to defaults for new avatar
      setAvatarForm({
        skinTone: '#FDBCB4',
        hairColor: '#8B4513',
        eyeColor: '#4A90E2',
        hat: '',
        shirt: '',
        pants: '',
        shoes: '',
        accessory: '',
        background: ''
      });
    }
    setEditAvatarOpen(true);
  };

  const handleSaveAvatar = async () => {
    if (!user || teams.length === 0) return;

    try {
      const teamId = teams[0].id;
      
      // Save or update avatar (saveUserAvatar handles both cases)
      await avatarService.saveUserAvatar(user.id, teamId, avatarForm);

      // Reload profile data to show updated avatar
      await loadProfileData();
      setEditAvatarOpen(false);
    } catch (error) {
      console.error('Failed to save avatar:', error);
      setError('Failed to save avatar. Please try again.');
    }
  };

  // Helper function to get client coordinates from mouse or touch event
  const getClientCoordinates = (event: MouseEvent | TouchEvent) => {
    if ('touches' in event) {
      return {
        x: event.touches[0]?.clientX || 0,
        y: event.touches[0]?.clientY || 0
      };
    } else {
      return {
        x: event.clientX,
        y: event.clientY
      };
    }
  };

  // Helper function to get responsive positioning based on screen size
  const getResponsivePosition = (basePositions: { x: number; y: number }[], screenWidth: number) => {
    if (screenWidth < 600) { // Mobile
      return basePositions.map(pos => ({ x: pos.x * 0.5, y: pos.y * 0.6 }));
    } else if (screenWidth < 900) { // Tablet
      return basePositions.map(pos => ({ x: pos.x * 0.75, y: pos.y * 0.8 }));
    } else { // Desktop
      return basePositions;
    }
  };

  const handleAvatarDragStart = (event: React.MouseEvent | React.TouchEvent) => {
    // Don't start container drag if avatar is being rotated
    if (isAvatarRotating) {
      return;
    }
    
    // Only prevent dragging if clicking on resize handle or buttons
    const target = event.target as HTMLElement;
    if (target.closest('[title="Drag to resize avatar"]') || 
        target.closest('button')) {
      return;
    }
    
    event.preventDefault();
    event.stopPropagation();
    
    const coords = getClientCoordinates(event.nativeEvent as MouseEvent | TouchEvent);
    setIsDraggingAvatar(true);
    setDragStart({
      x: coords.x,
      y: coords.y,
      position: avatarPosition
    });
  };

  const handleAvatarDragMove = (event: MouseEvent | TouchEvent) => {
    if (!isDraggingAvatar || !dragStart) return;

    event.preventDefault();
    
    const coords = getClientCoordinates(event);
    const deltaX = coords.x - dragStart.x;
    const deltaY = coords.y - dragStart.y;
    
    const newPosition = {
      x: dragStart.position.x + deltaX,
      y: dragStart.position.y + deltaY
    };
    
    setAvatarPosition(newPosition);
    
    // Save to localStorage
    localStorage.setItem('avatarPosition', JSON.stringify(newPosition));
  };

  const handleAvatarDragEnd = () => {
    setIsDraggingAvatar(false);
    setDragStart(null);
  };

  React.useEffect(() => {
    if (isDraggingAvatar) {
      // Add both mouse and touch event listeners
      document.addEventListener('mousemove', handleAvatarDragMove as EventListener);
      document.addEventListener('mouseup', handleAvatarDragEnd);
      document.addEventListener('touchmove', handleAvatarDragMove as EventListener, { passive: false });
      document.addEventListener('touchend', handleAvatarDragEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleAvatarDragMove as EventListener);
        document.removeEventListener('mouseup', handleAvatarDragEnd);
        document.removeEventListener('touchmove', handleAvatarDragMove as EventListener);
        document.removeEventListener('touchend', handleAvatarDragEnd);
      };
    }
  }, [isDraggingAvatar, dragStart]);

  // Decoration drag handlers
  const handleDecorationDragStart = (event: React.MouseEvent | React.TouchEvent, itemId: string) => {
    event.preventDefault();
    event.stopPropagation();
    
    const coords = getClientCoordinates(event.nativeEvent as MouseEvent | TouchEvent);
    const currentPos = decorationPositions[itemId] || { x: 0, y: 0 };
    setDraggedDecoration(itemId);
    setDecorationDragStart({
      x: coords.x,
      y: coords.y,
      position: currentPos
    });
  };

  const handleDecorationDragMove = (event: MouseEvent | TouchEvent) => {
    if (!draggedDecoration || !decorationDragStart) return;

    event.preventDefault();
    
    const coords = getClientCoordinates(event);
    const deltaX = coords.x - decorationDragStart.x;
    const deltaY = coords.y - decorationDragStart.y;
    
    const newPositions = {
      ...decorationPositions,
      [draggedDecoration]: {
        x: decorationDragStart.position.x + deltaX,
        y: decorationDragStart.position.y + deltaY
      }
    };
    
    setDecorationPositions(newPositions);
    
    // Save to localStorage
    localStorage.setItem('decorationPositions', JSON.stringify(newPositions));
  };

  const handleDecorationDragEnd = () => {
    setDraggedDecoration(null);
    setDecorationDragStart(null);
  };

  React.useEffect(() => {
    if (draggedDecoration) {
      // Add both mouse and touch event listeners
      document.addEventListener('mousemove', handleDecorationDragMove as EventListener);
      document.addEventListener('mouseup', handleDecorationDragEnd);
      document.addEventListener('touchmove', handleDecorationDragMove as EventListener, { passive: false });
      document.addEventListener('touchend', handleDecorationDragEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleDecorationDragMove as EventListener);
        document.removeEventListener('mouseup', handleDecorationDragEnd);
        document.removeEventListener('touchmove', handleDecorationDragMove as EventListener);
        document.removeEventListener('touchend', handleDecorationDragEnd);
      };
    }
  }, [draggedDecoration, decorationDragStart]);

  // Decoration resize handlers
  const handleDecorationResizeStart = (event: React.MouseEvent | React.TouchEvent, itemId: string) => {
    event.preventDefault();
    event.stopPropagation();
    
    const coords = getClientCoordinates(event.nativeEvent as MouseEvent | TouchEvent);
    const currentSize = decorationSizes[itemId] || { width: 200, height: 200 }; // Default for background
    const category = availableItems.find(item => item.id === itemId)?.category;
    if (category === 'accessory') {
      currentSize.width = 80;
      currentSize.height = 80;
    }
    
    setResizedDecoration(itemId);
    setDecorationResizeStart({
      x: coords.x,
      y: coords.y,
      size: currentSize
    });
  };

  const handleDecorationResizeMove = (event: MouseEvent | TouchEvent) => {
    if (!resizedDecoration || !decorationResizeStart) return;

    event.preventDefault();
    
    const coords = getClientCoordinates(event);
    const deltaX = coords.x - decorationResizeStart.x;
    const deltaY = coords.y - decorationResizeStart.y;
    const delta = Math.max(deltaX, deltaY); // Use the larger delta for proportional resizing
    
    const category = availableItems.find(item => item.id === resizedDecoration)?.category;
    
    // Responsive size constraints
    const getSizeConstraints = () => {
      if (category === 'accessory') {
        if (screenWidth < 600) { // Mobile
          return { min: 30, max: 120 };
        } else if (screenWidth < 900) { // Tablet
          return { min: 35, max: 160 };
        } else { // Desktop
          return { min: 40, max: 200 };
        }
      } else { // Background
        if (screenWidth < 600) { // Mobile
          return { min: 80, max: 250 };
        } else if (screenWidth < 900) { // Tablet
          return { min: 90, max: 320 };
        } else { // Desktop
          return { min: 100, max: 400 };
        }
      }
    };
    
    const constraints = getSizeConstraints();
    const minSize = constraints.min;
    const maxSize = constraints.max;
    
    const newSize = Math.max(minSize, Math.min(maxSize, decorationResizeStart.size.width + delta));
    
    const newSizes = {
      ...decorationSizes,
      [resizedDecoration]: {
        width: newSize,
        height: newSize
      }
    };
    
    setDecorationSizes(newSizes);
    localStorage.setItem('decorationSizes', JSON.stringify(newSizes));
  };

  const handleDecorationResizeEnd = () => {
    setResizedDecoration(null);
    setDecorationResizeStart(null);
  };

  React.useEffect(() => {
    if (resizedDecoration) {
      // Add both mouse and touch event listeners
      document.addEventListener('mousemove', handleDecorationResizeMove as EventListener);
      document.addEventListener('mouseup', handleDecorationResizeEnd);
      document.addEventListener('touchmove', handleDecorationResizeMove as EventListener, { passive: false });
      document.addEventListener('touchend', handleDecorationResizeEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleDecorationResizeMove as EventListener);
        document.removeEventListener('mouseup', handleDecorationResizeEnd);
        document.removeEventListener('touchmove', handleDecorationResizeMove as EventListener);
        document.removeEventListener('touchend', handleDecorationResizeEnd);
      };
    }
  }, [resizedDecoration, decorationResizeStart]);

  const handleEquipItem = async (itemId: string, category: 'hat' | 'shirt' | 'pants' | 'shoes' | 'accessory' | 'background') => {
    if (!user || teams.length === 0) return;

    try {
      const teamId = teams[0].id;
      
      // For coaches, check if they created this item - if so, allow equipping without purchase
      const item = availableItems.find(i => i.id === itemId);
      if ((user.role === 'coach' || user.role === 'headCoach') && item && item.coachId === user.id) {
        // Coach created this item, equip it directly
        await avatarService.saveUserAvatar(user.id, teamId, { [category]: itemId });
        await loadProfileData();
        setError(''); // Clear any previous errors
        return;
      }
      
      // For regular users or items not created by coach, use normal equip logic
      const result = await avatarService.equipAvatarItem(user.id, teamId, itemId, category);
      
      if (result.success) {
        // Reload profile data to show updated avatar
        await loadProfileData();
        setError(''); // Clear any previous errors
      } else {
        setError(result.message);
      }
    } catch (error) {
      console.error('Failed to equip item:', error);
      setError('Failed to equip item. Please try again.');
    }
  };

  const handleUnequipItem = async (category: 'hat' | 'shirt' | 'pants' | 'shoes' | 'accessory' | 'background') => {
    if (!user || teams.length === 0) return;

    try {
      const teamId = teams[0].id;
      
      // Clear the category field to unequip (hide) the item
      await avatarService.saveUserAvatar(user.id, teamId, { [category]: '' });
      
      // Reload profile data to show updated avatar
      await loadProfileData();
      setError(''); // Clear any previous errors
      setSuccess('Item removed from display!');
      
      // Clear success message after a delay
      setTimeout(() => setSuccess(''), 2000);
    } catch (error) {
      console.error('Failed to unequip item:', error);
      setError('Failed to remove item from display. Please try again.');
    }
  };


  const getLockedAssets = async () => {
    if (!user || teams.length === 0) return [];
    
    try {
      const shopItems = await avatarService.getShopItemsWithOwnership(user.id, teams[0].id);
      return shopItems
        .filter(item => item.rpmAssetId && !item.owned)
        .map(item => item.rpmAssetId)
        .filter((assetId): assetId is string => Boolean(assetId));
    } catch (error) {
      console.error('Failed to get locked assets:', error);
      return [];
    }
  };

  const handleIframeAvatarExported = async (avatarUrl: string, avatarId: string) => {
    if (!user || teams.length === 0) return;

    try {
      setLoading(true);
      
      console.log('Processing avatar from iframe:', { avatarUrl, avatarId });
      
      // Add cache-busting parameter to force refresh
      const cacheBustedUrl = `${avatarUrl}?t=${Date.now()}`;
      
      // Update the user's avatar data
      const updatedAvatar: UserAvatar = {
        ...userAvatar!,
        readyPlayerMeAvatarId: avatarId,
        readyPlayerMeUrl: cacheBustedUrl,
        avatarUrl: cacheBustedUrl,
        updatedAt: Timestamp.now()
      };
      
      console.log('Updated avatar data:', updatedAvatar);
      
      // Save to database
      await avatarService.saveUserAvatar(user.id, teams[0].id, updatedAvatar);
      
      // Update local state
      setUserAvatar(updatedAvatar);
      
      console.log('Avatar saved successfully from iframe');
      
      // Close the iframe modal
      setShowIframeModal(false);
      
      // Show success message
      setSuccess('Avatar updated successfully!');
      setError('');
      
      // Force a small delay to ensure the avatar component re-renders
      setTimeout(() => {
        console.log('Forcing avatar refresh...');
        setUserAvatar(prev => prev ? { ...prev, updatedAt: Timestamp.now() } : prev);
      }, 100);
      
    } catch (error) {
      console.error('Failed to save avatar from iframe:', error);
      setError('Failed to save avatar. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleManualAvatarUpdate = async (newAvatarUrl: string) => {
    if (!user || teams.length === 0) return;

    try {
      setLoading(true);
      
      // Extract avatar ID from URL
      const avatarId = newAvatarUrl.split('/').pop()?.replace('.glb', '');
      if (!avatarId) {
        setError('Invalid avatar URL format');
        return;
      }

      console.log('Updating avatar with ID:', avatarId);
      console.log('New avatar URL:', newAvatarUrl);

      // Add cache-busting parameter to force refresh
      const cacheBustedUrl = `${newAvatarUrl}?t=${Date.now()}`;

      // Update the avatar data
      const updatedAvatar: UserAvatar = {
        ...userAvatar!,
        readyPlayerMeAvatarId: avatarId,
        readyPlayerMeUrl: cacheBustedUrl,
        avatarUrl: cacheBustedUrl,
        updatedAt: Timestamp.now()
      };

      await avatarService.saveUserAvatar(user.id, teams[0].id, updatedAvatar);
      
      // Update the local state
      setUserAvatar(updatedAvatar);
      
      setSuccess('Avatar updated successfully!');
      setError('');
      
      // Force a small delay to ensure the avatar component re-renders
      setTimeout(() => {
        console.log('Forcing avatar refresh...');
        setUserAvatar(prev => prev ? { ...prev, updatedAt: Timestamp.now() } : prev);
      }, 100);
      
    } catch (error) {
      console.error('Failed to update avatar:', error);
      setError('Failed to update avatar. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarCreated = async (avatarUrl: string) => {
    if (!user || teams.length === 0) return;

    try {
      const teamId = teams[0].id;
      
      // Save the new Ready Player Me avatar URL
      await avatarService.saveUserAvatar(user.id, teamId, { 
        ...userAvatar,
        readyPlayerMeUrl: avatarUrl 
      });
      
      // Refresh profile data to show the new avatar
      await loadProfileData();
      
      console.log('Avatar saved successfully:', avatarUrl);
    } catch (error) {
      console.error('Error saving avatar:', error);
      setError('Failed to save avatar. Please try again.');
    }
  };

  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'common': return 'default';
      case 'rare': return 'primary';
      case 'epic': return 'secondary';
      case 'legendary': return 'warning';
      default: return 'default';
    }
  };

  const handleAddPR = () => {
    setEditingPR(null);
    setPrDialogOpen(true);
  };

  const handleEditPR = (pr: PersonalRecord) => {
    setEditingPR(pr);
    setPrDialogOpen(true);
  };

  const handleSavePR = async (prData: Partial<PersonalRecord>) => {
    if (!user) return;
    
    const teamId = teams.length > 0 ? teams[0].id : '';
    
    if (editingPR) {
      // Update existing PR
      await avatarService.updatePersonalRecord(editingPR.id, prData);
    } else {
      // Add new PR
      await avatarService.addPersonalRecord(user.id, teamId, {
        eventType: prData.eventType!,
        time: prData.time!,
        date: prData.date!,
        isVerified: false,
        ...prData,
      });
    }
    
    // Reload PRs
    await loadProfileData();
    setSuccess(editingPR ? 'PR updated successfully!' : 'PR added successfully!');
    setTimeout(() => setSuccess(''), 3000);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }


  const handleSavePrivacy = async (privacy: any) => {
    if (!user) return;
    
    try {
      await userService.updateProfilePrivacy(user.id, privacy);
      await refreshCurrentUser(); // Refresh AuthContext so next open shows saved settings
      setSuccess('Privacy settings updated successfully!');
    } catch (error) {
      console.error('Failed to save privacy settings:', error);
      setError('Failed to save privacy settings');
    }
  };

  const getUserPrivacy = () => {
    return user?.profilePrivacy || userService.getDefaultPrivacySettings();
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom>
              My Profile
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Customize your avatar, view achievements, and track your personal records
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<ShareIcon />}
              onClick={() => setShareDialogOpen(true)}
              size="small"
            >
              Share
            </Button>
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={() => setPrivacyDialogOpen(true)}
              size="small"
            >
              Privacy
            </Button>
          </Box>
        </Box>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="profile tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab 
            icon={<TrophyIcon />} 
            label="PRs" 
            id="profile-tab-0"
            aria-controls="profile-tabpanel-0"
          />
          <Tab 
            icon={<PointsIcon />} 
            label="Leaderboard" 
            id="profile-tab-1"
            aria-controls="profile-tabpanel-1"
          />
          <Tab 
            icon={<PrizesIcon />} 
            label="My Prizes" 
            id="profile-tab-2"
            aria-controls="profile-tabpanel-2"
          />
          {/* Challenges & Missions Tab - HIDDEN FOR NOW */}
          {/* <Tab 
            icon={<ChallengesIcon />} 
            label="Challenges & Missions" 
            id="profile-tab-3"
            aria-controls="profile-tabpanel-3"
          /> */}
          <Tab 
            icon={<TeamsIcon />} 
            label="My Teams" 
            id="profile-tab-4"
            aria-controls="profile-tabpanel-4"
          />
          <Tab 
            icon={<GoalsIcon />} 
            label="My Goals" 
            id="profile-tab-5"
            aria-controls="profile-tabpanel-5"
          />
          <Tab 
            icon={<HistoryIcon />} 
            label="Season History" 
            id="profile-tab-6"
            aria-controls="profile-tabpanel-6"
          />
          <Tab 
            icon={<SettingsIcon />} 
            label="Settings" 
            id="profile-tab-7"
            aria-controls="profile-tabpanel-7"
          />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {/* Badges - HIDDEN FOR NOW */}
          {/* <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Badges & Achievements
                </Typography>
                
                {userBadges.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {userBadges.map((userBadge) => {
                      const badge = availableBadges.find(b => b.id === userBadge.badgeId);
                      return (
                        <Chip
                          key={userBadge.id}
                          icon={<TrophyIcon />}
                          label={badge?.name || 'Unknown Badge'}
                          color={getRarityColor(badge?.rarity || 'common') as any}
                          variant="outlined"
                        />
                      );
                    })}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No badges earned yet
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid> */}

          {/* Personal Records */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Personal Records</Typography>
                  <IconButton onClick={handleAddPR} color="primary">
                    <AddIcon />
                  </IconButton>
                </Box>
                
                {userPRs.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {userPRs.map((pr) => (
                      <Box key={pr.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="bold">
                            {pr.eventType} {pr.isVerified && <Chip label="Verified" size="small" color="success" sx={{ ml: 1, height: 18 }} />}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {pr.location && `${pr.location} • `}
                            {new Date(pr.date.seconds * 1000).toLocaleDateString()}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="h6" color="primary">
                            {formatPRTimeDisplay(pr.time)}
                          </Typography>
                          <IconButton size="small" onClick={() => handleEditPR(pr)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No personal records set yet
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Tab 1: Points & Leaderboard */}
      <TabPanel value={tabValue} index={1}>
        <PointsPage refreshTrigger={0} />
      </TabPanel>

      {/* Tab 2: My Prizes */}
      <TabPanel value={tabValue} index={2}>
        <PrizesPage />
      </TabPanel>

      {/* Tab 3: Challenges & Missions - HIDDEN FOR NOW */}
      {/* <TabPanel value={tabValue} index={3}>
        <ChallengesMissionsPage />
      </TabPanel> */}

      {/* Tab 4: My Teams */}
      <TabPanel value={tabValue} index={3}>
        <TeamsPage />
      </TabPanel>

      {/* Tab 5: My Goals */}
      <TabPanel value={tabValue} index={4}>
        <GoalsPage />
      </TabPanel>

      {/* Tab 6: Season History */}
      <TabPanel value={tabValue} index={5}>
        {teams.length > 0 && (
          <SeasonHistorySection userId={user?.id || ''} teamId={teams[0].id} />
        )}
        {teams.length === 0 && (
          <Alert severity="info">
            You need to be part of a team to view season history.
          </Alert>
        )}
      </TabPanel>

      {/* Tab 7: Settings */}
      <TabPanel value={tabValue} index={6}>
        {/* Display Name - users can edit their own name */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Display Name
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  This name is shown on your profile and in team lists.
                </Typography>
                <Typography variant="body1">{user?.displayName || '—'}</Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setEditDisplayNameDialogOpen(true)}
              >
                Edit Name
              </Button>
            </Box>
          </CardContent>
        </Card>

        <NotificationSettings />
        <WorkflowSettings />

        {/* Help & Support */}
        <Box sx={{ mt: 4, pt: 4, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" gutterBottom>
            Help & Support
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Need assistance or have questions? Visit our help page for contact information and support.
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => navigate('/help')}
            startIcon={<HelpIcon />}
          >
            Get Help
          </Button>
        </Box>
        
        {/* Danger Zone - Account Deletion */}
        <Box sx={{ mt: 4, pt: 4, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" color="error" gutterBottom>
            Danger Zone
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Permanently delete your account and all associated data. This action cannot be undone.
          </Typography>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setDeleteAccountDialogOpen(true)}
            startIcon={<DeleteIcon />}
          >
            Delete My Account
          </Button>
        </Box>
      </TabPanel>

      {/* Edit Avatar Dialog */}
      <Dialog 
        open={editAvatarOpen} 
        onClose={() => setEditAvatarOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Customize Avatar</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            {/* Avatar Preview */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Preview</Typography>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                minHeight: 300,
                backgroundColor: avatarForm.background ? '#f5f5f5' : 'primary.light',
                borderRadius: 2,
                mb: 2
              }}>
                <Avatar
                  sx={{ 
                    width: 200, 
                    height: 200,
                    backgroundColor: avatarForm.skinTone,
                    border: '3px solid',
                    borderColor: avatarForm.eyeColor
                  }}
                >
                  <Typography variant="h2">
                    {user?.displayName?.charAt(0) || 'U'}
                  </Typography>
                </Avatar>
              </Box>
            </Grid>

            {/* Customization Options */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Customize</Typography>
              
              {/* Skin Tone */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Skin Tone</Typography>
                <TextField
                  type="color"
                  value={avatarForm.skinTone}
                  onChange={(e) => setAvatarForm({...avatarForm, skinTone: e.target.value})}
                  size="small"
                  sx={{ width: 60 }}
                />
              </Box>

              {/* Hair Color */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Hair Color</Typography>
                <TextField
                  type="color"
                  value={avatarForm.hairColor}
                  onChange={(e) => setAvatarForm({...avatarForm, hairColor: e.target.value})}
                  size="small"
                  sx={{ width: 60 }}
                />
              </Box>

              {/* Eye Color */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Eye Color</Typography>
                <TextField
                  type="color"
                  value={avatarForm.eyeColor}
                  onChange={(e) => setAvatarForm({...avatarForm, eyeColor: e.target.value})}
                  size="small"
                  sx={{ width: 60 }}
                />
              </Box>

              {/* Background Color */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Background</Typography>
                <TextField
                  type="color"
                  value={avatarForm.background}
                  onChange={(e) => setAvatarForm({...avatarForm, background: e.target.value})}
                  size="small"
                  sx={{ width: 60 }}
                />
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Note: Equipping items from your inventory will be available after purchasing them from the shop.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditAvatarOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveAvatar} variant="contained">
            {userAvatar ? 'Update Avatar' : 'Create Avatar'}
          </Button>
        </DialogActions>
      </Dialog>


      <AvatarCreatorModal
        open={avatarCreatorOpen}
        onClose={() => setAvatarCreatorOpen(false)}
        onAvatarCreated={handleAvatarCreated}
        currentAvatarUrl={userAvatar?.readyPlayerMeUrl}
      />

      {/* Equip Items Dialog */}
      <Dialog 
        open={equipItemsOpen} 
        onClose={() => setEquipItemsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Decorate Avatar Area
            <IconButton onClick={() => setEquipItemsOpen(false)}>
              <AddIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {(() => {
            // For coaches, show all items they created + purchased items
            // For athletes, show only purchased items
            let itemsToShow: AvatarItem[] = [];
            
            // Only show background and accessory items for decoration around the avatar
            const decorationItems = availableItems.filter(item => 
              item.category === 'background' || item.category === 'accessory'
            );
            
            if (user?.role === 'coach' || user?.role === 'headCoach') {
              // Show decoration items created by coach/headCoach + purchased decoration items
              const createdItems = decorationItems.filter(item => item.coachId === user.id);
              const purchasedItems = userInventory ? 
                decorationItems.filter(item => userInventory.items.includes(item.id)) : [];
              
              // Combine and remove duplicates
              const allItems = [...createdItems, ...purchasedItems];
              itemsToShow = allItems.filter((item, index, self) => 
                index === self.findIndex(i => i.id === item.id)
              );
            } else {
              // For athletes, only show purchased decoration items
              itemsToShow = userInventory ? 
                decorationItems.filter(item => userInventory.items.includes(item.id)) : [];
            }

            if (itemsToShow.length > 0) {
              return (
                <Grid container spacing={2}>
                  {itemsToShow.map((item) => {
                    const isEquipped = Boolean(userAvatar && (
                      (item.category === 'hat' && userAvatar.hat === item.id) ||
                      (item.category === 'shirt' && userAvatar.shirt === item.id) ||
                      (item.category === 'pants' && userAvatar.pants === item.id) ||
                      (item.category === 'shoes' && userAvatar.shoes === item.id) ||
                      (item.category === 'accessory' && userAvatar.accessory === item.id) ||
                      (item.category === 'background' && userAvatar.background === item.id)
                    ));

                    const isOwned = (user?.role === 'coach' || user?.role === 'headCoach') && item.coachId === user.id;
                    const isPurchased = userInventory && userInventory.items.includes(item.id);

                    return (
                      <Grid item xs={12} sm={6} md={4} key={item.id}>
                        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                          <Box sx={{ p: 2, flexGrow: 1 }}>
                            {item.imageUrl && (
                              <Box sx={{ textAlign: 'center', mb: 2 }}>
                                <img
                                  src={item.imageUrl}
                                  alt={item.name}
                                  style={{
                                    width: '100%',
                                    height: '120px',
                                    objectFit: 'cover',
                                    borderRadius: '8px'
                                  }}
                                />
                              </Box>
                            )}
                            <Typography variant="h6" gutterBottom>
                              {item.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              {item.description}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Chip 
                                label={item.category} 
                                size="small" 
                                variant="outlined" 
                              />
                              <Chip 
                                label={item.rarity} 
                                size="small" 
                                color={getRarityColor(item.rarity) as any}
                              />
                            </Box>
                            {(isOwned || isPurchased) && (
                              <Button
                                fullWidth
                                variant={isEquipped ? "contained" : "outlined"}
                                color={isEquipped ? "success" : "primary"}
                                onClick={() => handleEquipItem(item.id, item.category)}
                                disabled={isEquipped}
                              >
                                {isEquipped ? 'Equipped' : 'Equip'}
                              </Button>
                            )}
                            {!isOwned && !isPurchased && (
                              <Button
                                fullWidth
                                variant="outlined"
                                disabled
                              >
                                Not Owned
                              </Button>
                            )}
                          </Box>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              );
            } else {
              return (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No Items Available
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {(user?.role === 'coach' || user?.role === 'headCoach')
                      ? "You haven't created any background or accessory items yet. Create decoration items in the Manage Shop section."
                      : "You don't have any background or accessory items to equip. Purchase decoration items from the shop first."
                    }
                  </Typography>
                </Box>
              );
            }
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEquipItemsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ReadyPlayerMe Iframe Modal */}
      <Dialog
        open={showIframeModal}
        onClose={() => setShowIframeModal(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '800px',
          }
        }}
      >
        <DialogTitle>
          ReadyPlayerMe Avatar Creator
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Create or edit your avatar directly in this window. Your changes will be saved automatically.
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '100%' }}>
          {user && (
            <ReadyPlayerMeIframe
              userId={user.id}
              lockedAssets={lockedAssets}
              onAvatarExported={handleIframeAvatarExported}
              onClose={() => setShowIframeModal(false)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowIframeModal(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Profile Dialog */}
      {user && (
        <ShareProfileDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          userId={user.id}
          userName={user.displayName}
        />
      )}

      {/* Profile Privacy Dialog */}
      {user && (
        <ProfilePrivacyDialog
          open={privacyDialogOpen}
          onClose={() => setPrivacyDialogOpen(false)}
          currentPrivacy={getUserPrivacy()}
          onSave={handleSavePrivacy}
        />
      )}

      {/* Personal Record Dialog */}
      <AddEditPRDialog
        open={prDialogOpen}
        onClose={() => setPrDialogOpen(false)}
        onSave={handleSavePR}
        existingPR={editingPR}
        isCoachEditing={false}
      />

      {/* Edit Display Name Dialog - users edit their own name */}
      {user && (
        <EditDisplayNameDialog
          open={editDisplayNameDialogOpen}
          onClose={() => setEditDisplayNameDialogOpen(false)}
          currentDisplayName={user.displayName}
          onSave={async (newDisplayName) => {
            await userService.updateDisplayName(user.id, newDisplayName, { currentUserId: user.id });
            await refreshCurrentUser();
            setSuccess('Name updated successfully!');
            setTimeout(() => setSuccess(''), 3000);
          }}
          title="Edit Display Name"
          label="Display name"
        />
      )}

      {/* Delete Account Dialog */}
      <DeleteAccountDialog
        open={deleteAccountDialogOpen}
        onClose={() => setDeleteAccountDialogOpen(false)}
      />
    </Container>
  );
};

export default ProfilePage;
